import React, { useMemo, useState } from "react";
import { Flex, Text } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickProductCardComparison from "@/features/pathpick/components/product/PathPickProductCardComparison";
import PathPickProductCardRecommendation from "@/features/pathpick/components/product/PathPickProductCardRecommendation";
import PathPickMarginFormulaModal from "@/features/pathpick/components/product/PathPickMarginFormulaModal";
import {
  PathPickMarket,
  PathPickProductCardComparisonLayout,
} from "@/features/pathpick/types";
import {
  getPathPickMarketMetrics,
  getPathPickMarketRecommendation,
  PathPickMarketMetrics,
  PATH_PICK_MARKET_BASIS_LABEL_KEY,
} from "@/features/pathpick/utils/market";
import { LineSheetProduct } from "@/features/line-sheet-sets/line-sheet-set.types";
import {
  LineSheetProductMarketPrice,
  LineSheetSetMarketPricing,
} from "@/features/order-sheet-sets/hooks/use-line-sheet-set-market-prices";
import {
  getEstimatedSupplyBreakdown,
  ORDER_SHEET_MARKET_BASIS,
  toMarketProduct,
} from "@/features/order-sheet-sets/utils/order-sheet-market-product";
import EstimatedSupplyTooltip from "@/features/order-sheet-sets/order-sheet-estimated-supply-tooltip";

interface Props {
  // 공급가(supplyPrice)는 컨디션을 고치면 바뀐다 — 그 현재 값으로 예상 매입가를 계산한다.
  product: LineSheetProduct;
  marketPrice: LineSheetProductMarketPrice;
  pricing: LineSheetSetMarketPricing | undefined;
  // 나란히 보기(side) / 위아래로 보기(stack) — 카드 보기 설정에서 고른다.
  layout: PathPickProductCardComparisonLayout;
  onOpenSizes?: () => void;
}

// 기준가가 없으면 마진을 낼 수 없다 — 0 원 기준으로 계산된 가짜 마진을 지운다.
const withoutMargin = (metrics: PathPickMarketMetrics): PathPickMarketMetrics => ({
  ...metrics,
  expectedMargin: null,
  marginRate: null,
});

/**
 * 카드 보기의 크림·포이즌 비교 — 패스픽 상품 카드의 비교 블록을 그대로 쓴다.
 * (마켓 이름 ↗ / 기준가 / 거래수 / 예상 마진, 두 마켓 다 있으면 추천 줄, 마진 산식 안내)
 * 오더시트 상품을 패스픽 상품 모양으로 바꿔 넘기므로 값·색·동작이 패스픽 카드와 같다.
 * 위에는 마진의 기준인 예상 매입가를, 아래에는 사이즈별 시세 버튼을 둔다.
 */
export default function OrderSheetMarketComparison({
  product,
  marketPrice,
  pricing,
  layout,
  onOpenSizes,
}: Props) {
  const { t, tCurrencyString } = useI18nHelper();
  const [marginMarket, setMarginMarket] = useState<PathPickMarket | null>(null);

  const estimate = getEstimatedSupplyBreakdown(
    product.supplyPrice,
    marketPrice,
    pricing
  );
  const estimatedKrw = estimate?.value ?? null;
  const item = useMemo(
    () => toMarketProduct(product, marketPrice, estimatedKrw),
    [product, marketPrice, estimatedKrw]
  );
  const hasEstimate = estimatedKrw != null;
  const metricsByMarket = useMemo(() => {
    const kream = getPathPickMarketMetrics(item, "KREAM", ORDER_SHEET_MARKET_BASIS);
    const poizon = getPathPickMarketMetrics(item, "POIZON", ORDER_SHEET_MARKET_BASIS);
    return hasEstimate
      ? { KREAM: kream, POIZON: poizon }
      : { KREAM: withoutMargin(kream), POIZON: withoutMargin(poizon) };
  }, [item, hasEstimate]);
  const recommendation = getPathPickMarketRecommendation(
    metricsByMarket.KREAM,
    metricsByMarket.POIZON
  );
  const marginMetrics = marginMarket ? metricsByMarket[marginMarket] : null;

  return (
    // 카드를 누르면 선택이 토글된다 — 시세 블록 안의 클릭(링크·산식·사이즈별)은 선택으로 새지 않게 끊는다.
    <Flex
      flexDirection={"column"}
      gap={"8px"}
      minWidth={0}
      border={"0.5px solid #D4D4D1"}
      borderRadius={"8px"}
      padding={"8px 10px"}
      background={"#FFFFFF"}
      onClick={(e) => e.stopPropagation()}
    >
      <Flex justifyContent={"space-between"} alignItems={"center"} gap={"6px"}>
        <Text textStyle={"caption-bold"} color={"#1D1D1B"}>
          {t("estimated_landed_cost")}
        </Text>
        {/* 값에 올리면 계산식(공급가 × 부대비용 × 환율 + 수수료)을 보여준다 */}
        <EstimatedSupplyTooltip breakdown={estimate}>
          <Text
            textStyle={"caption-bold"}
            color={"#1D1D1B"}
            cursor={estimate ? "help" : undefined}
          >
            {estimatedKrw != null
              ? tCurrencyString(estimatedKrw, { type: "SYMBOL", code: "KRW" })
              : "-"}
          </Text>
        </EstimatedSupplyTooltip>
      </Flex>

      <PathPickProductCardComparison
        item={item}
        kreamMetrics={metricsByMarket.KREAM}
        poizonMetrics={metricsByMarket.POIZON}
        basis={ORDER_SHEET_MARKET_BASIS}
        layout={layout}
        onOpenMarginFormula={setMarginMarket}
      />
      {/* 한쪽 마켓만 매칭된 상품은 비교 대상이 없어 추천 줄을 그리지 않는다(패스픽과 같다). */}
      {recommendation && (
        <PathPickProductCardRecommendation
          recommendation={recommendation}
          layout={layout}
        />
      )}

      {onOpenSizes && (
        <Text
          as={"button"}
          type={"button"}
          alignSelf={"flex-end"}
          textStyle={"caption-medium"}
          color={"#1272EF"}
          textDecoration={"underline"}
          onClick={onOpenSizes}
        >
          {t("market_price_by_size")}
        </Text>
      )}

      {marginMarket && marginMetrics && (
        <PathPickMarginFormulaModal
          isOpen
          onClose={() => setMarginMarket(null)}
          market={marginMarket}
          item={item}
          supplyPrice={item.salePrice.value}
          basisValue={marginMetrics.basisValue}
          basisLabel={t(
            PATH_PICK_MARKET_BASIS_LABEL_KEY[marginMarket][ORDER_SHEET_MARKET_BASIS]
          )}
          expectedMargin={marginMetrics.expectedMargin}
          marginRate={marginMetrics.marginRate}
        />
      )}
    </Flex>
  );
}
